import { Children, type ReactNode } from 'react';
import { MarkdownRenderer } from './renderer';
import { KindLink, PageLink } from '@/components/PageLink';
import { ProductChip } from '@/components/ProductChip';

const CHIP_RE = /\[(product|page):([a-z0-9-]+)\]/g;
const PRODUCT_PATH_RE = /^\/produits\/([a-z0-9-]+)\/?$/i;

export function productSlugFromUrl(href: string | undefined): string | null {
  if (!href) return null;

  try {
    const isRelative = href.startsWith('/');
    const url = new URL(href, 'https://calebasse.com');
    if (!isRelative && url.hostname !== 'calebasse.com' && url.hostname !== 'www.calebasse.com') {
      return null;
    }
    return PRODUCT_PATH_RE.exec(url.pathname)?.[1]?.toLowerCase() ?? null;
  } catch {
    return null;
  }
}

/**
 * Pre-process raw markdown: rewrite `[product:slug]` / `[page:slug]` chip
 * patterns into markdown links with a custom URL scheme that the `a`
 * component override below can intercept.
 *
 * Why this approach: react-markdown 9 does not expose a text-node renderer.
 * Standard markdown link syntax is the most reliable AST node to override.
 */
export function preprocessChipsToLinks(source: string | null | undefined): string {
  // Streaming partials can produce sections whose body_md hasn't arrived yet.
  // Treat any non-string input as empty so we render an empty bubble instead
  // of crashing the whole React tree.
  if (typeof source !== 'string') return '';
  return source.replace(CHIP_RE, (_match, kind: string, slug: string) => {
    return `[${slug}](chatbot-${kind}:${slug})`;
  });
}

export interface ChipAwareMarkdownProps {
  source: string;
  /** Defaults to the in-widget ProductChip / PageLink. `label`: the name the reply wrote in its link. */
  renderProduct?: (slug: string, label?: string) => ReactNode;
  renderPage?: (slug: string) => ReactNode;
  className?: string;
}

/**
 * Markdown renderer that additionally replaces inline `[product:slug]`
 * and `[page:slug]` patterns with chip components (ProductChip / PageLink unless overridden).
 *
 * Implementation: pre-processes chips into markdown links with custom
 * URL schemes (`chatbot-product:` / `chatbot-page:`), then overrides the
 * `a` component to render the appropriate chip component when it sees
 * those schemes.
 */
export function ChipAwareMarkdown({
  source,
  renderProduct = (slug, label) => <ProductChip slug={slug} label={label} />,
  renderPage = (slug) => <PageLink slug={slug} />,
  className,
}: ChipAwareMarkdownProps): JSX.Element {
  const preprocessed = preprocessChipsToLinks(source);

  return (
    <MarkdownRenderer
      source={preprocessed}
      className={className}
      components={{
        a: ({ href, children }) => {
          if (typeof href === 'string') {
            if (href.startsWith('chatbot-product:')) {
              const slug = href.slice('chatbot-product:'.length);
              return <>{renderProduct(slug)}</>;
            }
            if (href.startsWith('chatbot-page:')) {
              const slug = href.slice('chatbot-page:'.length);
              return <>{renderPage(slug)}</>;
            }

            // Free-form/legacy replies use normal Calebasse product links
            // rather than [product:slug]. Resolve them through the same chip.
            const productSlug = productSlugFromUrl(href);
            if (productSlug) return <>{renderProduct(productSlug, linkName(children))}</>;
          }
          // Non-chip anchor — classify the URL by path so /blog/ and
          // /pages/ links get the same visual treatment as inline page
          // chips (green+book vs purple+file). Generic URLs fall back
          // to the neutral muted style.
          return <KindLink href={href}>{Children.toArray(children)}</KindLink>;
        },
      }}
    />
  );
}

/**
 * The name a reply wrote in its product link, as it wrote it ("le [Yu Ping Feng San](…)"): it
 * reads right in the sentence, and it is there while the reply streams, before the product
 * itself has come — so the line doesn't change at the end. A bare slug is no name.
 */
function linkName(children: ReactNode): string | undefined {
  const parts = Children.toArray(children);
  if (!parts.every((part) => typeof part === 'string')) return undefined;
  const name = parts.join('').trim();
  return name && !/^[a-z0-9-]+$/.test(name) ? name : undefined;
}
